import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { useInvestorGate } from "./useInvestorGate";
import { useInvestorSession } from "./useInvestorSession";
import { withInvestorQuery } from "../utils/investorRouteHelpers";
import { isInvestable } from "../utils/investorInvestmentsAdapter";

/**
 * Shared invest gating: opportunity readiness, then KYC redirect with detail-page return path.
 */
export function useInvestmentInvestFlow(opportunityId) {
  const navigate = useNavigate();
  const { ensureKycApproved } = useInvestorGate();
  const { isLoggedIn } = useInvestorSession();

  const detailPath = `/invest/investments/${opportunityId}`;

  const buildDetailPath = useCallback(
    (query = {}) => withInvestorQuery(detailPath, query),
    [detailPath]
  );

  const startInvest = useCallback(
    (onApproved, opportunity = null) => {
      if (opportunity && !isInvestable(opportunity)) {
        toast(
          opportunity.investUnavailableReason ||
            "This opportunity isn’t open for investment yet."
        );
        return false;
      }

      if (!isLoggedIn) {
        navigate(withInvestorQuery("/login", {
          redirect: detailPath,
          intent: "invest",
          itemId: opportunityId,
        }));
        return false;
      }

      if (
        !ensureKycApproved({
          redirect: detailPath,
          intent: "invest",
          itemId: opportunityId,
        })
      ) {
        toast("Complete KYC to invest");
        return false;
      }

      onApproved?.();
      return true;
    },
    [isLoggedIn, ensureKycApproved, navigate, detailPath, opportunityId]
  );

  const goToDetail = useCallback(() => {
    navigate(detailPath);
  }, [navigate, detailPath]);

  return { detailPath, buildDetailPath, startInvest, goToDetail };
}
